import { Component } from '@angular/core';
import { AbstractControl, FormBuilder, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { FuseConfirmDialogComponent } from '@fuse/components/confirm-dialog/confirm-dialog.component';
import { IdentityAuthorizeService } from '@fuse/services/identity-authorize.service';
import { ConfigService } from '@fuse/services/oidc-config.service';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { CookieService } from 'ngx-cookie-service';
import {trigger, state, style,animate, transition} from '@angular/animations';
import { environment as env } from "environments/environment";

@Component({
    selector: 'login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    animations: [
        trigger('slideInOut', [
            // Slide Down - Enter animation
            transition(':enter', [
                style({ opacity: 0, transform: 'translateY(-10px)' }),
                animate('500ms cubic-bezier(0.35, 0, 0.25, 1)', style({ opacity: 1, transform: 'translateY(0)' }))
            ]),
            // Slide Up - Leave animation
            transition(':leave', [
                animate('500ms cubic-bezier(0.35, 0, 0.25, 1)', style({ opacity: 0, transform: 'translateY(-10px)' }))
            ])
        ])
    ],
    standalone: false
})
export class LoginComponent {
    isLoginVisible = true;
    confirmDialogRef: MatDialogRef<FuseConfirmDialogComponent>;
    emailForm = this.fb.group({
        email: ['', [Validators.required, Validators.pattern(/^[a-zA-Z0-9.!#$%&'*+\/=?^_`{|}~-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/)]],
        rememberMe: [false]
    });
    get email(): AbstractControl {
        return this.emailForm.get('email');
      }
      removeSpaces(event: any) {
        const value = event.target.value.replace(/\s/g, '');
        this.emailForm.get('email')?.setValue(value, { emitEvent: false });
    }
    isPreLoginPostBack: boolean = false;
    validationMessage: string | null = null;
    private isProcessingLoginHint: boolean = false;
    private isAuthCheckComplete: boolean = false;
    
    constructor(private fb: FormBuilder,
        private identityAuthorizeService: IdentityAuthorizeService,
        private configService: ConfigService,
        private dialog: MatDialog,
        private _cookieService: CookieService,
        public oidcSecurityService: OidcSecurityService) { }

    ngOnInit(): void {
        // First priority: Check if user is already authenticated
        this.oidcSecurityService.checkAuth().subscribe((loginResponse) => {
            console.log('LoginComponent: checkAuth response:', loginResponse);
            this.isAuthCheckComplete = true;
            
            if (loginResponse?.isAuthenticated) {
                // User is already authenticated, ensure session is marked and redirect
                console.log('LoginComponent: User already authenticated, redirecting to:', env.redirectRoute);
                localStorage.setItem("userLoggedIn", "true");
                setTimeout(() => {
                    window.location.href = env.redirectRoute;
                }, 100);
                return;
            }
            
            // Check for postback flag first (after setUserIdentityConfig reload)
            const isPostBack = sessionStorage.getItem('isPreLoginPostBack');
            if (isPostBack === "true") {
                console.log('LoginComponent: Post-reload after identity config, triggering OIDC login');
                sessionStorage.removeItem('isPreLoginPostBack');
                this.isPreLoginPostBack = true;
                // Hide login form while OIDC authorization is in progress
                this.isLoginVisible = false;
                // Clear login_hint from URL to prevent reprocessing
                this.clearLoginHintFromUrl();
                
                // Verify configuration is ready before triggering authorization
                const userIdentity = this._cookieService.get('userIdentity');
                const userEmail = this._cookieService.get('userEmail');
                
                if (userIdentity && userEmail) {
                    console.log('LoginComponent: OIDC configuration ready. Identity:', userIdentity, 'Email:', userEmail);
                    // Configuration is ready, trigger OIDC authorization immediately
                    this.oidcSecurityService.authorize();
                } else {
                    console.warn('LoginComponent: Missing required cookies after postback. Identity:', userIdentity, 'Email:', userEmail);
                    // Reset state to allow normal login flow
                    this.isPreLoginPostBack = false;
                    this.isLoginVisible = true;
                }
                return;
            }
            // User is not authenticated, show login form
            this.isLoginVisible = true;
            
            // Process login_hint only if not a postback and not already processing
            this.processLoginHintIfPresent();
        });
    }

    /**
     * Securely process login_hint parameter with backend validation
     * This prevents direct SSO trigger based on untrusted user input
     */
    private processLoginHintIfPresent(): void {
        // Prevent reprocessing if already in progress
        if (this.isProcessingLoginHint) {
            console.log('LoginComponent: login_hint processing already in progress');
            return;
        }

        const loginHint = this.getLoginHintFromUrl();
        
        // Validate format before making backend call
        if (!loginHint || !this.isValidEmailFormat(loginHint)) {
            console.log('LoginComponent: login_hint not present or invalid format');
            return;
        }

        console.log('LoginComponent: Processing login_hint with backend validation');
        this.isProcessingLoginHint = true;
        this.isLoginVisible = false; // Hide form while processing

        // SECURITY: Validate with backend before proceeding
        this.identityAuthorizeService.getIdentityUserType(loginHint).subscribe({
            next: async (isAderantUser) => {
                console.log('LoginComponent: Backend validated login_hint, user type:', isAderantUser ? 'Aderant' : 'ALN');
                
                // Only set cookies after successful backend validation
                this._cookieService.set('userEmail', loginHint, 30);
                const identity = isAderantUser ? 'Aderant' : 'ALN';
                
                // Configure OIDC with validated identity
                await this.configService.setUserIdentityConfig(identity);
                // Note: setUserIdentityConfig triggers a page reload
            },
            error: (err) => {
                console.error('LoginComponent: Backend validation failed for login_hint:', err);
                // On validation failure, show login form for manual entry
                this.isProcessingLoginHint = false;
                this.isLoginVisible = true;
                this.validationMessage = 'Unable to validate email. Please try again.';
                this.autoHideValidationMessage();
                // Clear invalid login_hint from URL
                this.clearLoginHintFromUrl();
            }
        });
    }

    /**
     * Extract login_hint from URL query parameters
     */
    private getLoginHintFromUrl(): string | null {
        const urlParams = new URLSearchParams(window.location.search);
        return urlParams.get('login_hint');
    }

    /**
     * Validate email format using the same pattern as the form validator
     */
    private isValidEmailFormat(email: string): boolean {
        const emailPattern = /^[a-zA-Z0-9.!#$%&'*+\/=?^_`{|}~-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        return emailPattern.test(email);
    }

    /**
     * Remove login_hint from URL to prevent reprocessing
     */
    private clearLoginHintFromUrl(): void {
        const url = new URL(window.location.href);
        if (url.searchParams.has('login_hint')) {
            url.searchParams.delete('login_hint');
            window.history.replaceState({}, document.title, url.toString());
            console.log('LoginComponent: Cleared login_hint from URL');
        }
    }
    // Hide validation message after 10 seconds
    autoHideValidationMessage() {
        setTimeout(() => {
            this.validationMessage = null;
        }, 10000);
    }
    async getIdentityUserType() {
        if (this.emailForm.invalid) {
            // Set the error message to show in the red div
            if (this.email?.hasError('required') || this.email?.hasError('pattern')) {
                this.validationMessage = 'Valid Email is Required';
            }
            this.autoHideValidationMessage();
            return;
        }
        let email = this.emailForm.controls["email"].value;
        this.identityAuthorizeService.getIdentityUserType(email).subscribe(async isAderantUser => {
            this._cookieService.set('userEmail', email, 30);
            let rememberMe = this.emailForm.controls["rememberMe"].value;
            if (rememberMe) {
                // Save to the cookies
                this._cookieService.set('landingPageEmail', email, 30);

            }
            else {
                this._cookieService.delete('landingPageEmail');
            }
            let identity = isAderantUser ? 'Aderant' : 'ALN';
            await this.configService.setUserIdentityConfig(identity);
        }, err => {
            console.error('Error occurred while getting identity user type:', err);
        }, () => { })
    }
}
